import { api } from './api'

// Types for the bank data API response
export interface BankClient {
  id: number
  client_identifier: string
  first_name: string
  last_name: string
  date_of_birth: string
  designation: string
  company: string
  joining_date: string
  is_active: boolean
  risk_profile: string
  created_at: string
  updated_at: string
}

export interface BankContactInfo {
  id: number
  client_id: number
  client_identifier: string
  email_address: string
  emergency_contact_name: string
  address_line_1: string
  address_line_2: string
  street: string
  city: string
  state: string
  country: string
  mailing_zip_code: string
  social_security_number: string
  phone_number: string
  created_at: string
  updated_at: string
}

export interface BankAccount {
  id: number
  client_identifier: string
  account_identifier: string
  amount: string
  status: string
  account_type: string
  account_name: string
  created_at: string
  updated_at: string
  created_by: string
  updated_by: string
  deleted_at: string | null
  deleted_by: string | null
}

export interface BankData {
  clients: BankClient[]
  contact_info: BankContactInfo[]
  accounts: BankAccount[]
  total_clients: number
  total_contacts: number
  total_accounts: number
}

export interface BankDataResponse {
  success: boolean
  message: string
  schema_name: string
  data: BankData
  correlation_id: string
  timestamp: string
}

export interface CombinedBankData {
  bankA: BankDataResponse | null
  bankB: BankDataResponse | null
}

class BankDataService {
  /**
   * Get complete bank data from bank_a schema
   */
  async getBankAData(): Promise<BankDataResponse> {
    try {
      const response = await api.get('/demo-data/bank-complete/bank_a')
      return response.data
    } catch (error: any) {
      console.error('Error fetching bank A data:', error)
      throw new Error(error?.response?.data?.message || 'Failed to fetch bank A data')
    }
  }

  /**
   * Get complete bank data from bank_b schema
   */
  async getBankBData(): Promise<BankDataResponse> {
    try {
      const response = await api.get('/demo-data/bank-complete/bank_b')
      return response.data
    } catch (error: any) {
      console.error('Error fetching bank B data:', error)
      throw new Error(error?.response?.data?.message || 'Failed to fetch bank B data')
    }
  }

  /**
   * Get data from both banks simultaneously
   */
  async getCombinedBankData(): Promise<CombinedBankData> {
    try {
      const [bankAResult, bankBResult] = await Promise.allSettled([
        this.getBankAData(),
        this.getBankBData()
      ])

      return {
        bankA: bankAResult.status === 'fulfilled' ? bankAResult.value : null,
        bankB: bankBResult.status === 'fulfilled' ? bankBResult.value : null
      }
    } catch (error: any) {
      console.error('Error fetching combined bank data:', error)
      throw new Error('Failed to fetch bank data')
    }
  }
}

export const bankDataService = new BankDataService()